import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  School,
  Loader2,
  BriefcaseIcon,
  MapPin,
  Info,
  CheckCircle,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { scrollToError } from 'src/utils/form-utils';
import { Button } from 'src/components/ui/button';
import { MultiSelectDropdown } from 'src/components/common/MultiSelectDropdown';
import { Input } from 'src/components/ui/input';
import { FormField } from 'src/components/common/FormField';
import { validateResumeForm } from 'src/utils/resume-validation';
import { getUser, getProfileIsProcessed } from 'src/utils/authStorage';
import { formatIndianNumber } from 'src/utils/numberFormat';
import { trackEvent } from 'src/utils/analytics';
import { useFormAbandonmentTracking } from 'src/hooks/useFormAbandonmentTracking';
import { PersonalInfoSection } from '../../components/resume/PersonalInfoSection';
import {
  EducationFields,
  AddEducationButton,
} from '../../components/resume/EducationFields';
import {
  WorkExperienceFields,
  AddExperienceButton,
} from '../../components/resume/WorkExperienceFields';
import { Section, FormActions } from '../../components/resume/common';
import { SkillsSection } from '../../components/resume/SkillsSection';
import { CertificationsSection } from '../../components/resume/CertificationsSection';
import { defaultValues } from '../../components/resume/constants';
import createResumeApi from '../../components/resume/resumeApi';
import { ResumeService } from '../../services/api/ResumeService';

const validationRules = {
  // Personal Info
  firstname: { required: 'First name is required' },
  lastname: { required: 'Last name is required' },
  addressLine1: { required: 'Address Line 1 is required' },
  street: { required: 'Street is required' },
  city: { required: 'City is required' },
  email: {
    required: 'Email is required',
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: 'Invalid email address',
    },
  },
  contact: {
    required: 'Phone number is required',
    pattern: {
      value: /^\+?[\d\s-]{10,}$/,
      message: 'Invalid phone number',
    },
  },
  dob: { required: 'Date of birth is required' },
  fathersname: { required: "Father's name is required" },
  state: { required: 'State is required' },
  zipCode: { required: 'ZIP code is required' },
};
const educationValidation = {
  'educations.*.institution': { required: 'Institution name is required' },
  'educations.*.qualification': { required: 'Qualification is required' },
  'educations.*.graduationYear': { required: 'Year of passing is required' },
  'educations.*.fieldsOfStudy': { required: 'Field of study is required' },
  'educations.*.maxGPA': { required: 'Max GPA is required' },
  'educations.*.earnedGPA': { required: 'Earned GPA is required' },
};
// Constants
const INITIAL_MASTER_DATA = {
  skills: [],
  certifications: [],
  fieldsofstudies: [],
  fieldsofstudieshsc: [],
  city: [],
  state: [],
};

// Utility Functions
const getEmptyEducation = () => ({
  institution: '',
  qualification: '',
  graduationYear: '',
  fieldsOfStudy: '',
  gradeType: 'MARKS',
  maxMarks: '',
  earnedMarks: '',
  maxGPA: '',
  earnedGPA: '',
  board: '',
  university: '',
});

const getEmptyWorkExperience = () => ({
  company: '',
  role: '',
  employment_period: {
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    present: false,
  },
});

// --- Scroll to error helpers (from ResumeProfile) ---
function getFirstErrorField(errorsObj, prefix = '') {
  let result = null;
  Object.entries(errorsObj).some(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (
      typeof value === 'object' &&
      value !== null &&
      value.type === undefined
    ) {
      const nested = getFirstErrorField(value, path);
      if (nested) {
        result = nested;
        return true; // break
      }
    } else {
      result = path;
      return true; // break
    }
    return false;
  });
  return result;
}
function fieldNameToId(fieldName) {
  return fieldName.replace(/\./g, '-');
}

const generateKeys = {
  education: (edu, index) =>
    `education-${edu.institution || ''}-${edu.graduationYear || ''}-${index}`,
  workExperience: (exp, index) =>
    `work-${exp.company || ''}-${exp.role || ''}-${index}`,
};

const PROCESSING_TIMEOUT_SECONDS = 60;
const PROCESSING_POLL_INTERVAL_MS = 10000;

// Steps configuration for processing overlay
const PROCESSING_STEPS = [
  { label: 'Reading document structure…', pct: 20 },
  { label: 'Extracting personal details…', pct: 50 },
  { label: 'Parsing education & experience…', pct: 75 },
  { label: 'Filling in your form…', pct: 95 },
];

export default function SubmitResume() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState(defaultValues);
  const [masterData, setMasterData] = useState(INITIAL_MASTER_DATA);
  const [candidateId, setCandidateId] = useState(null);
  const [resumeApi, setResumeApi] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resumeProcessed, setResumeProcessed] = useState(false);
  const [processingCountdown, setProcessingCountdown] = useState(
    PROCESSING_TIMEOUT_SECONDS
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  // Set candidateId from stored user on mount
  useEffect(() => {
    const userData = getUser();
    setCandidateId(userData?.cid || null);
  }, []);

  const parseBool = (value) => value === true || value === 'true';

  const syncProcessingStatusFromProfile = React.useCallback(
    async ({ showErrorToast = false } = {}) => {
      if (!candidateId) return null;

      try {
        const profile = await ResumeService.getProfile(candidateId);
        const otherDetails = profile?.profile_data?.other_details || {};

        const profileIsProcessed = Boolean(otherDetails?.is_processed);
        const resumeProcessedValue = Boolean(otherDetails?.resume_processed);
        const profileIsComplete = Boolean(otherDetails?.is_complete);

        setProfileIsProcessed(profileIsProcessed);
        setResumeProcessed(resumeProcessedValue);
        setProfileIsComplete(profileIsComplete);

        return {
          profileIsProcessed,
          resumeProcessed: resumeProcessedValue,
        };
      } catch (error) {
        if (showErrorToast) {
          toast({
            message:
              error?.message ||
              'Unable to check processing status. Please try again.',
            variant: 'destructive',
          });
        }
        return null;
      }
    },
    [candidateId, toast]
  );

  // Show processing gate and auto-poll profile status every 10s for up to 60s
  useEffect(() => {
    if (!candidateId) return undefined;

    const processingDeadlineStorageKey = `submit_resume_processing_deadline_${candidateId}`;
    let isActive = true;
    let countdownIntervalId;
    let pollIntervalId;
    let timeoutId;

    const clearTimers = () => {
      if (countdownIntervalId) clearInterval(countdownIntervalId);
      if (pollIntervalId) clearInterval(pollIntervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };

    const shouldGateByStorage = () => {
      const profileIsProcessed = getProfileIsProcessed();
      return !profileIsProcessed && !resumeProcessed;
    };

    const readStoredDeadlineMs = () => {
      const rawValue = localStorage.getItem(processingDeadlineStorageKey);
      const parsed = Number(rawValue);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return parsed;
    };

    const writeStoredDeadlineMs = (deadlineMs) => {
      localStorage.setItem(processingDeadlineStorageKey, String(deadlineMs));
    };

    const clearStoredDeadlineMs = () => {
      localStorage.removeItem(processingDeadlineStorageKey);
    };

    const unlockGate = () => {
      if (!isActive) return;
      clearTimers();
      clearStoredDeadlineMs();

      // Start completion animation
      setIsCompleting(true);
      // Set all steps to completed
      setCurrentStep(PROCESSING_STEPS.length - 1);
      // Show success state
      setShowSuccess(true);

      // After showing completion animation, close the overlay
      setTimeout(() => {
        if (!isActive) return;
        setIsProcessing(false);
        setIsCompleting(false);
        setProcessingCountdown(PROCESSING_TIMEOUT_SECONDS);
        setCurrentStep(0);
        setShowSuccess(false);
      }, 2000); // 2 second delay to show completion
    };

    const startGate = (initialSeconds, deadlineMs) => {
      const requestedInitialSeconds =
        initialSeconds ?? PROCESSING_TIMEOUT_SECONDS;
      const safeInitialSeconds = Math.min(
        Math.max(Math.ceil(requestedInitialSeconds), 0),
        PROCESSING_TIMEOUT_SECONDS
      );
      const effectiveDeadlineMs =
        deadlineMs || Date.now() + safeInitialSeconds * 1000;

      writeStoredDeadlineMs(effectiveDeadlineMs);
      setIsProcessing(true);
      setProcessingCountdown(safeInitialSeconds);

      countdownIntervalId = setInterval(() => {
        if (!isActive) return;
        const remainingSeconds = Math.max(
          Math.ceil((effectiveDeadlineMs - Date.now()) / 1000),
          0
        );
        setProcessingCountdown(remainingSeconds);
      }, 1000);

      pollIntervalId = setInterval(async () => {
        const status = await syncProcessingStatusFromProfile();
        if (!status) return;

        const isReady = status.profileIsProcessed || status.resumeProcessed;
        if (isReady) {
          unlockGate();
        }
      }, PROCESSING_POLL_INTERVAL_MS);

      timeoutId = setTimeout(
        () => {
          if (!isActive) return;
          clearTimers();
          clearStoredDeadlineMs();
          setIsProcessing(false);
          setIsCompleting(false);
          setProcessingCountdown(0);
          setCurrentStep(0);
          setShowSuccess(false);
        },
        Math.max(effectiveDeadlineMs - Date.now(), 0)
      );
    };

    const bootstrap = async () => {
      if (!shouldGateByStorage()) {
        clearStoredDeadlineMs();
        setIsProcessing(false);
        setIsCompleting(false);
        return;
      }

      const storedDeadlineMs = readStoredDeadlineMs();
      const now = Date.now();

      if (storedDeadlineMs && storedDeadlineMs > now) {
        const remainingSeconds = Math.ceil((storedDeadlineMs - now) / 1000);
        startGate(remainingSeconds, storedDeadlineMs);
      } else if (storedDeadlineMs && storedDeadlineMs <= now) {
        clearStoredDeadlineMs();
        setIsProcessing(false);
        setIsCompleting(false);
        setProcessingCountdown(0);
        setCurrentStep(0);
        setShowSuccess(false);
      } else {
        startGate(PROCESSING_TIMEOUT_SECONDS);
      }

      const status = await syncProcessingStatusFromProfile();
      if (!status) return;

      const isReady = status.profileIsProcessed || status.resumeProcessed;
      if (isReady) {
        unlockGate();
      }
    };

    bootstrap();

    return () => {
      isActive = false;
      clearTimers();
    };
  }, [candidateId, syncProcessingStatusFromProfile]);

  // Clear terms error once accepted
  useEffect(() => {
    if (acceptedTerms && showTermsError) {
      setShowTermsError(false);
    }
  }, [acceptedTerms, showTermsError]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues,
    mode: 'onChange',
    rules: {
      ...validationRules,
      ...educationValidation,
    },
  });

  const { markCompleted } = useFormAbandonmentTracking(
    'Submit Resume',
    'resume-details',
    isDirty
  );

  // Compute dynamic count of missing required fields
  const getMissingRequiredCount = () => {
    let count = 0;
    // Personal info required fields
    if (!watch('firstname')) count += 1;
    if (!watch('lastname')) count += 1;
    if (!watch('addressLine1')) count += 1;
    if (!watch('street')) count += 1;
    if (!watch('city')) count += 1;
    if (!watch('email')) count += 1;
    if (!watch('contact')) count += 1;
    if (!watch('dob')) count += 1;
    if (!watch('fathersname')) count += 1;
    if (!watch('state')) count += 1;
    if (!watch('zipCode')) count += 1;

    // Preferred locations
    const pref = watch('preferredLocations');
    if (!pref || pref.length === 0) count += 1;

    // Education required rows: at least institution, qualification, graduationYear, and conditional fields
    const edus = watch('educations') || [];
    edus.forEach((edu) => {
      if (!edu?.institution) count += 1;
      if (!edu?.qualification) count += 1;
      if (!edu?.graduationYear) count += 1;
      if (['TWELFTH', 'DEGREE', 'MASTERS'].includes(edu?.qualification || '')) {
        if (!edu?.fieldsOfStudy) count += 1;
      }
      if ((edu?.gradeType || 'MARKS').toUpperCase() === 'MARKS') {
        if (!edu?.maxMarks) count += 1;
        if (!edu?.earnedMarks) count += 1;
      } else {
        if (!edu?.maxGPA) count += 1;
        if (!edu?.earnedGPA) count += 1;
      }
      if (['TENTH', 'TWELFTH'].includes(edu?.qualification || '')) {
        if (!edu?.board) count += 1;
      } else if (!edu?.university) {
        count += 1;
      }
    });

    // Work experiences are optional as a group; each row becomes required only if any field has value
    const exps = watch('workExperiences') || [];
    exps.forEach((exp) => {
      const hasAny = !!(
        exp?.company ||
        exp?.role ||
        exp?.employment_period?.startMonth ||
        exp?.employment_period?.startYear ||
        exp?.employment_period?.endMonth ||
        exp?.employment_period?.endYear ||
        exp?.employment_period?.present
      );
      if (hasAny) {
        if (!exp?.company) count += 1;
        if (!exp?.role) count += 1;
        const ep = exp?.employment_period || {};
        const isPresent = !!ep.present;
        const hasStart = ep.startMonth && ep.startYear;
        const hasEnd = ep.endMonth && ep.endYear;
        if (!hasStart) count += 1;
        if (!isPresent && !hasEnd) count += 1;
      }
    });

    return count;
  };

  // Create resumeApi when candidateId is available
  useEffect(() => {
    if (candidateId) {
      const api = createResumeApi({
        isProtected: true,
        candidateId,
      });
      setResumeApi(api);
    } else {
      setResumeApi(null);
    }
  }, [candidateId]);

  // Education Handlers
  const handleAddEducation = () => {
    const currentValues = watch();
    const updatedEducations = [
      ...currentValues.educations,
      getEmptyEducation(),
    ];

    reset({
      ...currentValues,
      educations: updatedEducations,
    });
    setFormData((prev) => ({ ...prev, educations: updatedEducations }));
  };

  const handleRemoveEducation = (index) => {
    const currentValues = watch();
    const updatedEducations = currentValues.educations.filter(
      (_, i) => i !== index
    );

    if (updatedEducations.length === 0) {
      updatedEducations.push(getEmptyEducation());
    }

    reset({
      ...currentValues,
      educations: updatedEducations,
    });
    setFormData((prev) => ({
      ...prev,
      educations: updatedEducations,
    }));
  };

  // Work Experience Handlers
  const handleAddWorkExperience = () => {
    const currentValues = watch();
    const updatedWorkExperiences = [
      ...(currentValues.workExperiences || []),
      getEmptyWorkExperience(),
    ];

    reset({
      ...currentValues,
      workExperiences: updatedWorkExperiences,
    });
    setFormData((prev) => ({
      ...prev,
      workExperiences: updatedWorkExperiences,
    }));
  };

  const handleRemoveWorkExperience = (index) => {
    const currentValues = watch();
    const updatedWorkExperiences = currentValues.workExperiences.filter(
      (_, i) => i !== index
    );

    if (updatedWorkExperiences.length === 0) {
      updatedWorkExperiences.push(getEmptyWorkExperience());
    }

    reset({
      ...currentValues,
      workExperiences: updatedWorkExperiences,
    });
    setFormData((prev) => ({
      ...prev,
      workExperiences: updatedWorkExperiences,
    }));
  };

  const loadFormData = async () => {
    try {
      setIsLoading(true);
      const mappedData = await resumeApi.fetchResumeData();

      const initialData = {
        ...mappedData,
        educations:
          mappedData.educations?.length > 0
            ? mappedData.educations
            : [getEmptyEducation()],
        workExperiences:
          mappedData.workExperiences?.length > 0
            ? mappedData.workExperiences
            : [getEmptyWorkExperience()],
        preferredLocations: Array.isArray(mappedData.preferredLocations)
          ? mappedData.preferredLocations.map((loc) => loc.id.toString())
          : [],
      };

      setFormData(initialData);
      setMasterData(mappedData.masterData || INITIAL_MASTER_DATA);
      reset(initialData);
    } catch (error) {
      console.error('Error loading data:', error);
      const fallbackData = location.state?.formData || {
        ...defaultValues,
        educations: [getEmptyEducation()],
        workExperiences: [getEmptyWorkExperience()],
      };
      setFormData(fallbackData);
      setMasterData(INITIAL_MASTER_DATA);
      reset(fallbackData);
      toast({
        message:
          error.message || 'Error loading resume data. Please try again later',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Centralized validation
  const validateForm = (data) => {
    const result = validateResumeForm(data);
    if (!result.isValid) {
      return (
        result.errors || { form: { type: 'manual', message: result.message } }
      );
    }
    return {};
  };

  // Update handleSubmitForm
  const handleSubmitForm = async (data) => {
    try {
      // Validate form
      const validationErrors = validateForm(data);
      if (Object.keys(validationErrors).length > 0) {
        // Check for qualification error and show toast
        if (
          validationErrors.educations &&
          validationErrors.educations.qualification &&
          validationErrors.educations.qualification.message
        ) {
          toast({
            message: validationErrors.educations.qualification.message,
            variant: 'destructive',
          });
          return;
        }
        scrollToError(validationErrors);
        return;
      }

      // API authentication check
      if (!resumeApi) {
        toast({
          message: 'Authentication required. Please log in to continue',
          variant: 'destructive',
        });
        navigate('/join-us');
        return;
      }

      setIsLoading(true);

      const filteredWorkExperiences = data.workExperiences.filter(
        (exp) =>
          exp.company ||
          exp.role ||
          exp.employment_period.startMonth ||
          exp.employment_period.startYear ||
          exp.employment_period.endMonth ||
          exp.employment_period.endYear
      );

      // Transform preferences fields for payload
      const expectedCtc = data.expectedCTC
        ? Number(data.expectedCTC)
        : undefined;
      const currentCtc = data.currentCTC ? Number(data.currentCTC) : undefined;
      const preferredWorkLocations = Array.isArray(data.preferredLocations)
        ? data.preferredLocations
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id))
        : [];
      const noticePeriod = data.noticePeriod
        ? Number(data.noticePeriod)
        : undefined;

      await resumeApi.updateResume({
        ...data,
        workExperiences:
          filteredWorkExperiences.length > 0 ? filteredWorkExperiences : [],
        expectedCtc,
        currentCtc,
        preferredWorkLocations,
        noticePeriod,
      });

      trackEvent('form_success', 'Submit Resume', 'resume-details');
      markCompleted();
      toast({ message: 'Resume updated successfully', variant: 'success' });
      navigate('/profile-dashboard');
    } catch (error) {
      trackEvent('form_error', 'Submit Resume', 'resume-details');
      toast({
        message:
          error.message || 'Error updating resume. Please try again later',
        variant: 'destructive',
      });

      // If it's a validation error, scroll to the error
      if (error.validationErrors) {
        scrollToError(error.validationErrors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate progress (must be before early return for hooks)
  const elapsed = PROCESSING_TIMEOUT_SECONDS - processingCountdown;
  const baseProgress = Math.min(
    Math.max((elapsed / PROCESSING_TIMEOUT_SECONDS) * 100, 0),
    100
  );
  // Show 100% progress during completion animation
  const progress = isCompleting ? 100 : baseProgress;

  // Update current step based on progress (must be before early return)
  useEffect(() => {
    if (!isProcessing && !isCompleting) {
      setCurrentStep(0);
      setShowSuccess(false);
      return undefined;
    }

    // During completion, ensure all steps are marked as done
    if (isCompleting) {
      setCurrentStep(PROCESSING_STEPS.length - 1);
      setShowSuccess(true);
      return undefined;
    }

    // Determine current step based on progress
    let newStep = 0;
    for (let i = PROCESSING_STEPS.length - 1; i >= 0; i -= 1) {
      if (progress >= PROCESSING_STEPS[i].pct) {
        newStep = i;
        break;
      }
    }
    setCurrentStep(newStep);

    // Show success when processing completes naturally (reaches 100%)
    if (progress >= 100 && isProcessing) {
      setShowSuccess(true);
      const successTimer = setTimeout(() => {
        setShowSuccess(false);
      }, 1800);
      return () => {
        clearTimeout(successTimer);
      };
    }
    return undefined;
  }, [progress, isProcessing, isCompleting]);

  // Initial Data Load
  useEffect(() => {
    if (!resumeApi) {
      // Don't do anything until resumeApi is ready
      return;
    }
    loadFormData();
  }, [resumeApi]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-full bg-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-sm text-gray-600">Loading resume data...</p>
      </div>
    );
  }

  // --- Replace form submit handler with scroll-to-error logic ---
  const onSubmitWithScroll = handleSubmit(
    (data) => {
      trackEvent('form_submit', 'Submit Resume', 'resume-details');
      return handleSubmitForm(data);
    },
    (formErrors) => {
      trackEvent('form_submit', 'Submit Resume', 'resume-details');
      const firstErrorField = getFirstErrorField(formErrors);
      if (firstErrorField) {
        const fieldId = fieldNameToId(firstErrorField);
        setTimeout(() => {
          const el = document.getElementById(fieldId);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus?.();
          }
        }, 100);
      }
    }
  );

  // Primary action click handler to support dynamic "Next" and "Update Details" behavior
  const handlePrimaryActionClick = () => {
    if (isProcessing) {
      return;
    }
    const missingCount = getMissingRequiredCount();
    if (missingCount > 0) {
      // Trigger validation to scroll to the first error field
      setShowTermsError(false);
      onSubmitWithScroll();
      return;
    }
    if (!acceptedTerms) {
      // No missing fields, but terms not accepted → scroll to terms checkbox
      setShowTermsError(true);
      const termsEl = document.getElementById('terms');
      if (termsEl) {
        termsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        termsEl.focus?.();
      }
      return;
    }
    // All good → submit
    onSubmitWithScroll();
  };

  return (
    <div className="flex justify-center items-center bg-gradient-to-b from-blue-50 via-white to-blue-50">
      <div className="container mx-auto px-4 py-8 pb-28 md:pb-28 max-w-4xl">
        {(isProcessing || isCompleting) && (
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,30,70,0.45)] backdrop-blur-[3px] p-4">
            <div
              className={`pointer-events-auto w-full max-w-[380px] rounded-2xl bg-white p-8 pb-7 text-center shadow-[0_20px_60px_rgba(15,30,70,0.22)] transition-all duration-[350ms] ${
                isProcessing
                  ? 'translate-y-0 scale-100'
                  : 'translate-y-3 scale-[0.97]'
              }`}
            >
              {!showSuccess ? (
                <div>
                  {/* Spinner Icon */}
                  <div className="relative mx-auto mb-[18px] h-[52px] w-[52px] rounded-full bg-ah-surface-process-icon flex items-center justify-center">
                    <div className="absolute inset-[-4px] rounded-full border-[3px] border-transparent border-t-ah-border-progress-active border-r-ah-border-progress-active animate-spin" />
                    <svg
                      className="h-[26px] w-[26px]"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--ah-text-process-active)"
                      strokeWidth="1.8"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  </div>

                  {/* Heading */}
                  <h2 className="mb-1 text-base font-semibold text-ah-text-darkest">
                    Analyzing your resume
                  </h2>
                  <p className="mb-[22px] min-h-[18px] text-ah-xs-step text-ah-text-muted transition-opacity duration-300">
                    {currentStep < PROCESSING_STEPS.length
                      ? PROCESSING_STEPS[currentStep].label
                      : 'Extracting your details — just a moment'}
                  </p>

                  {/* Steps List */}
                  <div className="mb-[22px] flex flex-col gap-2 text-left">
                    {PROCESSING_STEPS.map((step, index) => {
                      // During completion, all steps should be marked as done
                      const isDone = isCompleting ? true : index < currentStep;
                      const isActive = isCompleting
                        ? false
                        : index === currentStep;

                      // Determine text color class
                      let textColorClass = 'text-ah-text-disabled';
                      if (isActive) {
                        textColorClass = 'font-medium text-ah-text-darkest';
                      } else if (isDone) {
                        textColorClass = 'text-ah-status-success-dark';
                      }

                      // Determine step dot styles
                      let dotBgClass = 'bg-ah-surface-progress';
                      let dotBorderClass = 'border-ah-border-progress';
                      if (isActive) {
                        dotBgClass = 'bg-ah-surface-process-icon';
                        dotBorderClass = 'border-ah-border-progress-active';
                      } else if (isDone) {
                        dotBgClass = 'bg-ah-status-success-bg';
                        dotBorderClass = 'border-ah-status-success-dark';
                      }

                      // Determine step label text
                      const stepLabels = [
                        'Reading document structure',
                        'Extracting personal details',
                        'Parsing education & experience',
                        'Filling in your form',
                      ];

                      return (
                        <div
                          key={`step-${step.pct}-${step.label}`}
                          className={`flex items-center gap-[10px] text-ah-xs-step transition-colors duration-[400ms] ${textColorClass}`}
                        >
                          <div
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-[400ms] ${dotBgClass} ${dotBorderClass}`}
                          >
                            {isDone && (
                              <svg
                                className="h-[10px] w-[10px]"
                                viewBox="0 0 12 12"
                                fill="none"
                                stroke="var(--ah-status-success-dark)"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="2 6 5 9 10 3" />
                              </svg>
                            )}
                            {!isDone && isActive && (
                              <div className="h-1.5 w-1.5 rounded-full bg-ah-border-progress-active animate-pulse" />
                            )}
                          </div>
                          <span>{stepLabels[index]}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-[10px] h-1.5 overflow-hidden rounded-full bg-ah-surface-progress">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-ah-border-progress-active to-[#3b74f5] transition-all duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-ah-xs-number text-ah-text-disabled">
                    <span>
                      {currentStep < PROCESSING_STEPS.length
                        ? PROCESSING_STEPS[currentStep].label
                        : 'Processing…'}
                    </span>
                    <span className="font-semibold text-ah-xs-step text-ah-text-process-active">
                      {Math.round(progress)}%
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Success Icon */}
                  <div className="mx-auto mb-[18px] flex h-[52px] w-[52px] items-center justify-center rounded-full bg-ah-status-success-bg">
                    <svg
                      className="h-[26px] w-[26px]"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      stroke="var(--ah-status-success-dark)"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>

                  {/* Success Heading */}
                  <h2 className="mb-1 text-base font-semibold text-ah-text-darkest">
                    All set!
                  </h2>
                  <p className="mb-[22px] text-ah-xs-step text-ah-text-muted">
                    Your details have been filled in successfully.
                  </p>

                  {/* Success Progress Bar */}
                  <div className="mb-[10px] h-1.5 overflow-hidden rounded-full bg-ah-surface-progress">
                    <div className="h-full w-full rounded-full bg-gradient-to-r from-ah-status-success-dark to-ah-status-success-light" />
                  </div>
                  <div className="flex justify-between text-ah-xs-number text-ah-text-disabled">
                    <span className="font-medium text-ah-status-success-dark">
                      Resume processed
                    </span>
                    <span className="font-semibold text-ah-xs-step text-ah-status-success-dark">
                      100%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <form
          onSubmit={onSubmitWithScroll}
          className={`space-y-4 text-left relative transition-filter duration-300 ${
            isProcessing ? 'blur-[2px] pointer-events-none' : ''
          }`}
          noValidate
        >
          <fieldset
            disabled={isProcessing || isCompleting}
            className="space-y-4"
          >
            <PersonalInfoSection
              control={control}
              errors={errors}
              masterData={masterData}
            />
            <Section
              title="Educational Details"
              className="p-6"
              icon={<School className="mr-2 h-4 w-4 text-ah-brand-accent" />}
            >
              <div className="space-y-6">
                {formData.educations?.map((edu, index) => (
                  <EducationFields
                    key={generateKeys.education(edu, index)}
                    education={edu}
                    index={index}
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    errors={errors}
                    masterData={masterData}
                    onRemove={
                      (formData.educations?.length || 0) > 1
                        ? () => handleRemoveEducation(index)
                        : null
                    }
                  />
                ))}
                <AddEducationButton onClick={handleAddEducation} />
              </div>
            </Section>

            <Section
              title="Work Experience"
              className="p-6"
              icon={
                <BriefcaseIcon className="mr-2 h-4 w-4 text-ah-brand-accent" />
              }
            >
              <div className="space-y-6">
                {formData.workExperiences?.map((exp, index) => (
                  <WorkExperienceFields
                    key={generateKeys.workExperience(exp, index)}
                    experience={exp}
                    index={index}
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    errors={errors}
                    onRemove={
                      (formData.workExperiences?.length || 0) > 1
                        ? () => handleRemoveWorkExperience(index)
                        : null
                    }
                  />
                ))}
                <AddExperienceButton onClick={handleAddWorkExperience} />
              </div>
            </Section>

            {/* Preferences Section */}
            <Section
              title="Preferences"
              icon={<MapPin className="mr-2 h-4 w-4 text-" />}
              className="p-6"
            >
              <div className="relative space-y-4">
                {/* Preferred Location (Multi-Select) */}
                <FormField
                  label="Preferred Location"
                  required
                  className="text-xs"
                >
                  <Controller
                    name="preferredLocations"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value && value.length > 0
                          ? true
                          : 'At least one location is required',
                    }}
                    defaultValue={formData.preferredLocations || []}
                    render={({ field: { onChange, value } }) => {
                      // Transform masterData.preferedLocations to options
                      const locationOptions = (
                        masterData?.preferedLocations || []
                      ).map((loc) => ({
                        value: loc.id.toString(),
                        label: loc.name,
                      }));
                      return (
                        <MultiSelectDropdown
                          id="preferredLocations"
                          options={locationOptions}
                          placeholder="Select preferred locations..."
                          onChange={(selected) => {
                            onChange(selected.map((item) => item.value));
                          }}
                          value={locationOptions.filter((option) =>
                            value?.includes(option.value)
                          )}
                          maxDisplay={3}
                        />
                      );
                    }}
                  />
                  {errors?.preferredLocations && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.preferredLocations.message}
                    </p>
                  )}
                </FormField>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Current CTC (Optional) */}
                  <FormField label="Current CTC (Annual)" className="text-xs">
                    <Controller
                      name="currentCTC"
                      control={control}
                      defaultValue={formData.currentCTC || ''}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter current CTC"
                          inputMode="numeric"
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(
                              /\D/g,
                              ''
                            );
                            const limited = digitsOnly.slice(0, 8);
                            field.onChange(limited);
                          }}
                          value={formatIndianNumber(field.value)}
                          className="text-xs"
                        />
                      )}
                    />
                  </FormField>

                  {/* Expected CTC (Optional) */}
                  <FormField label="Expected CTC (Annual)" className="text-xs">
                    <Controller
                      name="expectedCTC"
                      control={control}
                      defaultValue={formData.expectedCTC || ''}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter expected CTC"
                          inputMode="numeric"
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(
                              /\D/g,
                              ''
                            );
                            const limited = digitsOnly.slice(0, 8);
                            field.onChange(limited);
                          }}
                          value={formatIndianNumber(field.value)}
                          className="text-xs"
                        />
                      )}
                    />
                  </FormField>
                  {/* Notice Period (days) 1-99 */}
                  <FormField label="Notice Period (days)" className="text-xs">
                    <Controller
                      name="noticePeriod"
                      control={control}
                      defaultValue={formData.noticePeriod || ''}
                      rules={{
                        validate: (value) => {
                          if (
                            value === undefined ||
                            value === null ||
                            value === ''
                          )
                            return true;
                          const n = Number(value);
                          return (
                            (Number.isInteger(n) && n >= 0 && n <= 99) ||
                            'Enter 1–99 days'
                          );
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min="1"
                          max="99"
                          step="1"
                          placeholder="Enter notice period"
                          inputMode="numeric"
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(
                              /\D/g,
                              ''
                            );
                            const limited = digitsOnly.slice(0, 2);
                            if (limited === '') {
                              field.onChange('');
                              return;
                            }
                            const clamped = Math.min(
                              parseInt(limited, 10),
                              99
                            ).toString();
                            field.onChange(clamped);
                          }}
                          className="text-xs"
                        />
                      )}
                    />
                    {errors?.noticePeriod && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.noticePeriod.message}
                      </p>
                    )}
                  </FormField>
                </div>
              </div>
            </Section>

            <SkillsSection control={control} masterData={masterData} />
            <CertificationsSection control={control} masterData={masterData} />
            <FormActions
              acceptedTerms={acceptedTerms}
              setAcceptedTerms={setAcceptedTerms}
              showTermsError={showTermsError}
              className="mb-20"
            />
            <div className="fixed inset-x-0 bottom-0 z-20 bg-white border-t border-gray-200 flex justify-between items-center py-3 px-4 md:px-[15%] lg:px-[23%]">
              <span className="text-sm text-gray-500 flex items-center">
                {getMissingRequiredCount() > 0 ? (
                  <>
                    <Info className="mr-2 h-4 w-4 text-red-500" />
                    {`${getMissingRequiredCount()} required field(s) are missing`}
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                    Ready to submit
                  </>
                )}
              </span>
              <Button
                type="button"
                onClick={handlePrimaryActionClick}
                className="bg-primary hover:bg-ah-brand-primary-hover"
                disabled={isProcessing || isCompleting}
              >
                {getMissingRequiredCount() === 0 && acceptedTerms
                  ? 'Update Details'
                  : 'Next'}
              </Button>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  );
}
